import { useState } from "react";
import { toast } from "sonner";
import { completeScanningOfDocuments } from "../../api/discussion";

type ScanningOfDocumentsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

/**
 * Scanning of Documents - Filing step view
 * Figma: Frame 1000005758 — centered layout with "Scanning Process" and upload instructions
 * Pixel-perfect: 22px gap, 20px gaps, Inter 16px/12px, #222222
 */
export default function ScanningOfDocumentsView({
  leadId,
  onSuccess,
}: ScanningOfDocumentsViewProps) {
  const [saving, setSaving] = useState(false);

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      await completeScanningOfDocuments(leadId);
      toast.success("Scanning details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px] justify-center py-8 md:py-12 px-4 md:px-0"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-[12px] md:text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center w-full gap-4 md:gap-5 px-4 md:px-0"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {/* Outer column */}
      <div className="flex flex-col items-center w-full max-w-[1085px] gap-4 md:gap-5">
        {/* Scanning Process - title */}
        <div
          className="flex items-center text-[#222222] text-base md:text-[16px] leading-5 font-normal w-full md:w-auto text-center md:text-left"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          Scanning Process
        </div>

        {/* Message container - responsive width */}
        <div className="flex flex-row justify-center items-center w-full">
          <div className="flex flex-col justify-center items-center w-full max-w-[457px] gap-4 md:gap-5 min-h-[37px]">
            <p
              className="flex items-center text-center text-[#222222] text-[12px] leading-5 font-normal w-full max-w-[457px]"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Scan the signed Power of Attorney and User Affidavit (if
              applicable) as PDF files. Ensure files are clear and under 5MB for
              upload.
            </p>
          </div>
        </div>

        {/* Save & Continue is provided by the parent footer */}
        <form
          id="scanning-of-documents-form"
          onSubmit={handleSaveAndContinue}
          className="sr-only"
          aria-hidden
        />
      </div>
    </div>
  );
}
